import React from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { SalesData, TicketType } from '../../types/PostProduction';

interface SalesDataSectionProps {
  salesData: SalesData;
  editMode: boolean;
  onRefreshFromTito: () => Promise<void>;
  onChange: (field: string, value: any) => void;
  onNestedChange: (field: string, subfield: string, value: any, index?: number) => void;
}

const SalesDataSection: React.FC<SalesDataSectionProps> = ({
  salesData,
  editMode,
  onRefreshFromTito,
  onChange,
  onNestedChange,
}) => {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" gutterBottom>Sales & Attendance Data</Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={onRefreshFromTito}
        >
          Refresh from Tito
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Tito Sales"
            type="number"
            fullWidth
            value={salesData.titoSales}
            onChange={(e) => onChange('titoSales', Number(e.target.value))}
            disabled={!editMode}
            margin="normal"
          />
          <TextField
            label="Walk-up Sales"
            type="number"
            fullWidth
            value={salesData.walkupSales}
            onChange={(e) => onChange('walkupSales', Number(e.target.value))}
            disabled={!editMode}
            margin="normal"
            helperText="Number of people at the door"
          />
          <TextField
            label="Walk-up Revenue (€)"
            type="number"
            fullWidth
            value={salesData.walkupRevenue}
            onChange={(e) => onChange('walkupRevenue', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Cash/card payments at the door"
          />
          <TextField
            label="No-Shows"
            type="number"
            fullWidth
            value={salesData.noShows}
            onChange={(e) => onChange('noShows', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Tickets sold but didn't attend"
          />
          <TextField
            label="Guest Attendance"
            type="number"
            fullWidth
            value={salesData.guests}
            onChange={(e) => onChange('guests', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Comps, industry passes, etc."
          />
          <TextField
            label="Total Attendance"
            type="number"
            fullWidth
            value={salesData.totalAttendance}
            disabled={true}
            margin="normal"
            helperText="Auto-calculated: Tito + Walk-up + Guests"
          />
          <TextField
            label="Tito Revenue (€)"
            type="number"
            fullWidth
            value={salesData.titoRevenue}
            disabled={true}
            margin="normal"
            helperText="From online ticket sales"
          />
          <TextField
            label="Gross Revenue (€)"
            type="number"
            fullWidth
            value={salesData.grossRevenue}
            disabled={true}
            margin="normal"
            helperText="Auto-calculated: Tito + Walk-up"
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            label="Refunds (€)"
            type="number"
            fullWidth
            value={salesData.refunds}
            onChange={(e) => onChange('refunds', Number(e.target.value))}
            disabled={!editMode}
            margin="normal"
          />
          <TextField
            label="Net Revenue (€)"
            type="number"
            fullWidth
            value={salesData.netRevenue}
            onChange={(e) => onChange('netRevenue', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Sales minus tax (enter manually)"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Ticket Type Breakdown
          </Typography>

          {salesData.ticketTypes.map((ticket, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Ticket Type"
                    fullWidth
                    value={ticket.name}
                    onChange={(e) => onNestedChange('ticketTypes', 'name', e.target.value, index)}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Quantity Sold"
                    type="number"
                    fullWidth
                    value={ticket.quantity}
                    onChange={(e) => onNestedChange('ticketTypes', 'quantity', Number(e.target.value), index)}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Price (€)"
                    type="number"
                    fullWidth
                    value={ticket.price}
                    onChange={(e) => onNestedChange('ticketTypes', 'price', Number(e.target.value), index)}
                    disabled={!editMode}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}

          {salesData.noShows > 0 && (
            <Box sx={{ mb: 2, bgcolor: 'rgba(255, 100, 100, 0.1)', p: 2, borderRadius: 1, border: '1px solid rgba(255, 100, 100, 0.3)' }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="No-Shows"
                    fullWidth
                    value="No-Shows"
                    disabled={true}
                    InputProps={{
                      style: { color: '#ff6b6b' }
                    }}
                    InputLabelProps={{
                      style: { color: 'rgba(255, 107, 107, 0.7)' }
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Quantity"
                    type="number"
                    fullWidth
                    value={salesData.noShows}
                    disabled={true}
                    InputProps={{
                      style: { color: '#ff6b6b' }
                    }}
                    InputLabelProps={{
                      style: { color: 'rgba(255, 107, 107, 0.7)' }
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="% of Tito Sales"
                    fullWidth
                    value={`${((salesData.noShows / (salesData.titoSales || 1)) * 100).toFixed(1)}%`}
                    disabled={true}
                    InputProps={{
                      style: { color: '#ff6b6b' }
                    }}
                    InputLabelProps={{
                      style: { color: 'rgba(255, 107, 107, 0.7)' }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          <Box sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', p: 2, borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Walk-up Sales"
                  fullWidth
                  value="Walk-up Sales"
                  disabled={true}
                  InputProps={{
                    style: { color: '#fff' }
                  }}
                  InputLabelProps={{
                    style: { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Quantity"
                  type="number"
                  fullWidth
                  value={salesData.walkupSales}
                  disabled={true}
                  InputProps={{
                    style: { color: '#fff' }
                  }}
                  InputLabelProps={{
                    style: { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Price (€)"
                  fullWidth
                  value="—"
                  disabled={true}
                  InputProps={{
                    style: { color: '#fff' }
                  }}
                  InputLabelProps={{
                    style: { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesDataSection;